import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Input,
  CircularProgress,
  Button,
  Sheet,
  Table,
  Checkbox,
  Select,
  Option,
} from '@mui/joy';
import { ChevronLeft, Key } from 'lucide-react';
import { authFetch } from '../../utils/authFetch';
import { canEditUtenti } from '../../utils/permissions';

import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

export default function DettaglioUtente() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [permessi, setPermessi] = useState({});
  const [userType, setUserType] = useState(1);

  const selectedOrg = localStorage.getItem('selectedOrg');

  const loggedUser = JSON.parse(localStorage.getItem('user') || 'null');
  const loggedUserId = loggedUser?.user?.id;

  const isSelf = Number(user?.id) === Number(loggedUserId);
  const canEditThisUser = canEditUtenti() && !isSelf;

  const livelli = ['Nessun accesso', 'Visualizzatore', 'Editor'];

  const menuLabels = {
    2: 'Categorie',
    3: 'Mezzi',
    4: 'Utenti',
    5: 'Checklist',
    8: 'Organizzazione',
  };

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const res = await authFetch(
          `${process.env.REACT_APP_SERVER_HOST}/org/${selectedOrg}/user/${id}`,
          { credentials: 'include' }
        );

        if (!res.ok) throw new Error('Errore fetch utente');

        const data = await res.json();
        setUser(data);
        setUserType(Number(data.user_type) || 1);

        if (data.permessi_json) {
          const parsed = JSON.parse(data.permessi_json);
          const map = {};
          parsed.forEach((perm) => {
            map[perm.menu_id] = perm.tipo;
          });
          setPermessi(map);
        }
      } catch (err) {
        console.error('Errore nel fetch utente:', err);
        toast.error('Errore nel caricamento utente', {
          position: 'top-right',
          autoClose: 3000,
        });
      } finally {
        setLoading(false);
      }
    };

    fetchUser();
  }, [selectedOrg, id]);

  const handlePermessoChange = (menuId, tipo) => {
    if (isSelf) return;

    setPermessi((prev) => ({
      ...prev,
      [menuId]: tipo,
    }));
  };

  const handleResetPassword = async () => {
    if (!user || !selectedOrg || isSelf) return;

    try {
      const res = await authFetch(
        `${process.env.REACT_APP_SERVER_HOST}/org/${selectedOrg}/user/${id}/reset-password`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
        }
      );

      if (!res.ok) throw new Error('Errore nel reset della password');

      toast.success('Password resettata con successo', {
        position: 'top-right',
        autoClose: 3000,
      });
    } catch (err) {
      console.error('Errore reset password:', err);
      toast.error('Errore nel reset della password', {
        position: 'top-right',
        autoClose: 3000,
      });
    }
  };

  const handleToggleStatus = async () => {
    if (!user || !selectedOrg || isSelf) return;

    const nuovoStatus = user.user_status === 1 ? 0 : 1;

    try {
      const res = await authFetch(
        `${process.env.REACT_APP_SERVER_HOST}/org/${selectedOrg}/user/${id}/status`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            status: nuovoStatus,
            user_id: parseInt(id, 10),
            org_id: parseInt(selectedOrg, 10),
          }),
        }
      );

      if (!res.ok) throw new Error('Errore nella modifica dello stato utente');

      setUser((prev) => ({
        ...prev,
        user_status: nuovoStatus,
      }));

      toast.success(
        nuovoStatus === 1
          ? 'Utente attivato con successo'
          : 'Utente disattivato con successo',
        { position: 'top-right', autoClose: 3000 }
      );
    } catch (err) {
      console.error('Errore toggle stato utente:', err);
      toast.error('Errore nel cambio di stato utente', {
        position: 'top-right',
        autoClose: 3000,
      });
    }
  };

  const handleUserTypeChange = async (_, newValue) => {
    if (!user || !selectedOrg || isSelf || !newValue) return;

    const nuovoTipo = Number(newValue);

    try {
      const res = await authFetch(
        `${process.env.REACT_APP_SERVER_HOST}/org/${selectedOrg}/user/${id}/type`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            user_type: nuovoTipo,
            user_id: parseInt(id, 10),
            org_id: parseInt(selectedOrg, 10),
          }),
        }
      );

      if (!res.ok) throw new Error('Errore nella modifica del tipo utente');

      setUserType(nuovoTipo);
      setUser((prev) => ({
        ...prev,
        user_type: nuovoTipo,
      }));

      toast.success('Tipo utente aggiornato con successo', {
        position: 'top-right',
        autoClose: 3000,
      });
    } catch (err) {
      console.error('Errore cambio tipo utente:', err);
      toast.error('Errore nel cambio del tipo utente', {
        position: 'top-right',
        autoClose: 3000,
      });
    }
  };

  const handleSavePermessi = async () => {
    if (!user || !selectedOrg || isSelf) return;

    const permessiArray = Object.entries(permessi).map(([menu_id, tipo]) => ({
      menu_id: parseInt(menu_id, 10),
      tipo,
    }));

    try {
      const res = await authFetch(
        `${process.env.REACT_APP_SERVER_HOST}/org/${selectedOrg}/user/${id}/permessi`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            permessi_json: permessiArray,
          }),
        }
      );

      if (!res.ok) throw new Error('Errore nel salvataggio dei permessi');

      toast.success('Permessi aggiornati', {
        position: 'top-right',
        autoClose: 3000,
      });
    } catch (err) {
      console.error('Errore aggiornamento permessi:', err);
      toast.error('Errore nel salvataggio dei permessi', {
        position: 'top-right',
        autoClose: 3000,
      });
    }
  };

  return (
    <Box>
      <Typography level="h3" mb={1}>
        Dettaglio Utente
      </Typography>

      <Button
        startDecorator={<ChevronLeft />}
        onClick={() => navigate(-1)}
        variant="outlined"
        size="sm"
        sx={{ mb: 3 }}
      >
        Indietro
      </Button>

      {loading ? (
        <CircularProgress />
      ) : !user ? (
        <Typography color="danger">Utente non trovato.</Typography>
      ) : (
        <Box display="flex" flexDirection={{ xs: 'column', md: 'row' }} gap={4}>
          <Sheet
            variant="outlined"
            sx={{
              flex: 1,
              p: 3,
              borderRadius: 'md',
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  borderRadius: '50%',
                  bgcolor: user.user_status === 1 ? 'success.500' : 'danger.500',
                }}
              />
              <Typography level="body-md">
                {user.user_status === 1 ? 'Attivo' : 'Disattivo'}
              </Typography>
            </Box>

            <Input disabled value={user.nome || ''} />
            <Input disabled value={user.username || ''} />
            <Input disabled value={user.codice_fiscale || ''} />

            <Box>
              <Typography level="body-sm" mb={0.5}>
                Tipo utente
              </Typography>

              <Select
                value={userType}
                onChange={handleUserTypeChange}
                disabled={!canEditThisUser}
              >
                <Option value={1}>Volontario</Option>
                <Option value={2}>Dipendente</Option>
              </Select>
            </Box>

            {canEditThisUser && (
              <Button
                color={user.user_status === 1 ? 'danger' : 'success'}
                onClick={handleToggleStatus}
              >
                {user.user_status === 1 ? 'Disattiva' : 'Attiva'}
              </Button>
            )}

            {canEditThisUser && (
              <Button onClick={handleResetPassword} startDecorator={<Key />}>
                Reset Password
              </Button>
            )}

            {isSelf && (
              <Typography level="body-sm" color="warning">
                Non puoi modificare il tuo utente
              </Typography>
            )}
          </Sheet>

          <Sheet variant="outlined" sx={{ flex: 1, p: 3, borderRadius: 'md' }}>
            <Typography level="title-md" mb={2}>
              Permessi
            </Typography>

            <Table size="sm">
              <thead>
                <tr>
                  <th>Sezione</th>
                  {livelli.map((liv, i) => (
                    <th key={i}>{liv}</th>
                  ))}
                </tr>
              </thead>

              <tbody>
                {Object.keys(menuLabels).map((menuId) => {
                  const menuNumericId = parseInt(menuId, 10);
                  const tipo = permessi[menuNumericId] ?? 0;

                  return (
                    <tr key={menuNumericId}>
                      <td>{menuLabels[menuNumericId]}</td>

                      {livelli.map((_, livello) => (
                        <td key={livello}>
                          <Checkbox
                            checked={tipo === livello}
                            disabled={isSelf}
                            onChange={() => handlePermessoChange(menuNumericId, livello)}
                          />
                        </td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </Table>

            {canEditThisUser && (
              <Button onClick={handleSavePermessi} sx={{ mt: 2 }}>
                Salva Permessi
              </Button>
            )}
          </Sheet>
        </Box>
      )}

      <ToastContainer />
    </Box>
  );
}